import { useId } from 'react'

import { formatLocalClock } from '../../lib/format'
import { dayAxisLayout } from '../../lib/stateGeometry'
import { useBoxSize } from '../state/useBoxSize'

export interface DayAxisReading {
  id: string
  time: string | null | undefined
  daily: boolean
  word: string | null
  kind: string
}

/** A still picture with a complete text alternative; no value controls colour. */
export function DayAxis({ readings }: { readings: readonly DayAxisReading[] }) {
  const [box, size] = useBoxSize<HTMLDivElement>()
  const description = useId()
  const layout = dayAxisLayout(readings, size.width || 600)
  return (
    <div className="day-axis" ref={box}>
      <div className="visually-hidden" id={description}>
        Readings on a time axis from {layout.start} to {layout.end}.
        <ol aria-label="Readings by time">
          {readings.map((reading) => (
            <li key={reading.id}>
              {formatLocalClock(reading.time)} · {reading.word} · {reading.kind}
            </li>
          ))}
        </ol>
      </div>
      <div className="day-axis__picture" aria-hidden="true" style={{ height: layout.height }} data-testid="day-axis">
        <span className="day-axis__line" style={{ top: layout.axisY }} />
        {layout.marks.map((mark) => {
          const reading = readings.find((r) => r.id === mark.id)!
          return (
            <div key={mark.id}>
              <span className="day-axis__flag" style={{ left: mark.labelLeft, top: mark.labelY, width: mark.labelWidth, minHeight: mark.labelHeight }}>
                {reading.word}
                <small>{reading.kind}</small>
              </span>
              <span
                className={reading.daily ? 'day-axis__mark day-axis__mark--daily' : 'day-axis__mark'}
                style={{ left: mark.x, top: mark.y }}
                data-recording-id={reading.id}
                data-daily={reading.daily}
              />
            </div>
          )
        })}
        {layout.ticks.map((tick) => <span className="day-axis__tick" key={tick.text} style={{ left: tick.x }}>{tick.text}</span>)}
      </div>
    </div>
  )
}
